import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  PauseCircleFilled as PauseIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, addHours, addDays, setHours, setMinutes } from "date-fns";
import { listPauseReasons, pauseTicket } from "../../services/ticketPauseService";

const useStyles = makeStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
  },
  shortcuts: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  shortcutChip: {
    cursor: "pointer",
  },
  reasonColor: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
  },
}));

const PauseTicketModal = ({ open, onClose, ticketId, onPaused }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const [pauseUntil, setPauseUntil] = useState("");
  const [autoResume, setAutoResume] = useState(false);

  useEffect(() => {
    if (!open) return;
    loadReasons();
    // Reset form
    setReasonId("");
    setNote("");
    setPauseUntil("");
    setAutoResume(false);
  }, [open]);

  const loadReasons = async () => {
    try {
      const data = await listPauseReasons(true);
      setReasons(data);
      // Selecionar o padrão
      const defaultReason = data.find((r) => r.isDefault);
      if (defaultReason) setReasonId(defaultReason.id);
    } catch (err) {
      console.error("Erro ao carregar motivos:", err);
    }
  };

  const handleShortcut = (hoursFromNow, label) => {
    let target;
    if (label === "Amanhã 09h") {
      target = setMinutes(setHours(addDays(new Date(), 1), 9), 0);
    } else if (label === "Amanhã 14h") {
      target = setMinutes(setHours(addDays(new Date(), 1), 14), 0);
    } else {
      target = addHours(new Date(), hoursFromNow);
    }
    setPauseUntil(format(target, "yyyy-MM-dd'T'HH:mm"));
    setAutoResume(true);
  };

  const handleSubmit = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const ticket = await pauseTicket(ticketId, {
        pauseReasonId: reasonId || undefined,
        pauseNote: note || undefined,
        pauseUntil: pauseUntil || undefined,
        pauseAutoResume: autoResume,
      });
      toast.success("Ticket pausado!");
      onPaused && onPaused(ticket);
      onClose();
    } catch (err) {
      console.error("Erro ao pausar:", err);
      const msg = err?.response?.data?.error || "Erro ao pausar ticket";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PauseIcon style={{ color: "#FF9800" }} />
          Pausar Atendimento
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          {/* Motivo da pausa */}
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Motivo da pausa</InputLabel>
            <Select
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
              label="Motivo da pausa"
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {reasons.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {reason.color && (
                      <span
                        className={classes.reasonColor}
                        style={{ backgroundColor: reason.color }}
                      />
                    )}
                    {reason.name}
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Observação */}
          <TextField
            label="Observação (opcional)"
            variant="outlined"
            size="small"
            multiline
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            placeholder="Ex: Cliente vai confirmar o pagamento até às 16h"
          />

          {/* Data/hora de retorno */}
          <TextField
            label="Retorno previsto (opcional)"
            variant="outlined"
            size="small"
            type="datetime-local"
            value={pauseUntil}
            onChange={(e) => setPauseUntil(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          {/* Atalhos de data */}
          <div>
            <Typography variant="caption" color="textSecondary">
              Atalhos:
            </Typography>
            <div className={classes.shortcuts}>
              <Chip
                className={classes.shortcutChip}
                label="Daqui 1h"
                size="small"
                variant="outlined"
                onClick={() => handleShortcut(1, "1h")}
              />
              <Chip
                className={classes.shortcutChip}
                label="Daqui 2h"
                size="small"
                variant="outlined"
                onClick={() => handleShortcut(2, "2h")}
              />
              <Chip
                className={classes.shortcutChip}
                label="Daqui 4h"
                size="small"
                variant="outlined"
                onClick={() => handleShortcut(4, "4h")}
              />
              <Chip
                className={classes.shortcutChip}
                label="Amanhã 09h"
                size="small"
                variant="outlined"
                onClick={() => handleShortcut(0, "Amanhã 09h")}
              />
              <Chip
                className={classes.shortcutChip}
                label="Amanhã 14h"
                size="small"
                variant="outlined"
                onClick={() => handleShortcut(0, "Amanhã 14h")}
              />
            </div>
          </div>

          {/* Auto-resume */}
          <FormControlLabel
            control={
              <Switch
                checked={autoResume}
                onChange={(e) => setAutoResume(e.target.checked)}
                color="primary"
              />
            }
            label="Retomar automaticamente na data prevista"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          style={{
            backgroundColor: "#FF9800",
            color: "#fff",
          }}
          startIcon={loading ? <CircularProgress size={16} /> : <PauseIcon />}
        >
          Pausar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PauseTicketModal;
